import { Select } from "antd";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import {
  clearSendBulkSMS,
  sendAvailabilityRequest,
  sendBookingDetails,
  sendBookingHoursQuery,
  sendBulkSMS,
  sendCalendarSummary,
  sendCalendarUpdated,
  sendNoWorkTomorrow
} from "@redux/actionCreators";
import SmsHistory from "@ui/communication-history/smsHistory";
import SendMessageModal, {
  SEND_MESSAGE_TAB,
  SMS_HISTORY_TAB,
  TEMPLATE_TAB
} from "@ui/messaging/send-message-modal";
import {
  AVAILABILITY_REQUEST,
  BOOKING_DETAILS,
  CALENDAR_SUMMARY,
  CALENDAR_UPDATED,
  NO_WORK_TOMORROW,
  REQUEST_HRS
} from "@ui/messaging/send-message-modal/use-message-templates";

const TAB_ORDER = [SEND_MESSAGE_TAB, TEMPLATE_TAB, SMS_HISTORY_TAB];

const SLICE_BY_PENDING = {
  freeText: "sendBulkSMSInfo",
  [AVAILABILITY_REQUEST]: "sendAvailabilityRequestInfo",
  [NO_WORK_TOMORROW]: "sendNoWorkTomorrowInfo",
  [CALENDAR_UPDATED]: "sendCalendarUpdatedInfo",
  [CALENDAR_SUMMARY]: "sendCalendarSummaryInfo",
  [BOOKING_DETAILS]: "sendBookingDetailsInfo",
  [REQUEST_HRS]: "sendBookingHoursQueryInfo"
};

const PERSON_TEMPLATE_ACTIONS = {
  [AVAILABILITY_REQUEST]: sendAvailabilityRequest,
  [NO_WORK_TOMORROW]: sendNoWorkTomorrow,
  [CALENDAR_UPDATED]: sendCalendarUpdated
};

const selectedPersonId = recipientIds =>
  recipientIds.length > 1 ? recipientIds : recipientIds[0];

const dispatchTemplate = (dispatch, templateId, recipientIds) => {
  const personObjectId = selectedPersonId(recipientIds);

  if (templateId === CALENDAR_SUMMARY) {
    dispatch(
      sendCalendarSummary({
        PersonObjectId: personObjectId,
        Weeks: 1,
        GetTemplateOnly: false
      })
    );
    return true;
  }

  const sendTemplate = PERSON_TEMPLATE_ACTIONS[templateId];
  if (!sendTemplate) {
    return false;
  }

  dispatch(sendTemplate({ personObjectId, template: false }));
  return true;
};

const dispatchBookingTemplate = (dispatch, templateId, bookingObjectIds) => {
  const ids = (bookingObjectIds || []).filter(Boolean);
  if (!ids.length) {
    return false;
  }

  if (templateId === BOOKING_DETAILS) {
    dispatch(
      sendBookingDetails({
        PromptForConfirm: false,
        GetTemplateOnly: false,
        BookingObjectIds: ids
      })
    );
    return true;
  }

  if (templateId === REQUEST_HRS) {
    dispatch(
      sendBookingHoursQuery({
        GetTemplateOnly: false,
        BookingObjectIds: ids
      })
    );
    return true;
  }

  return false;
};

const CandidateTableMessageModal = ({
  personObjectId,
  candidateObjectId,
  candidateName,
  recipients: recipientsProp,
  bookingObjectIds,
  hiddenTemplates,
  defaultTab = SEND_MESSAGE_TAB,
  tabOrder = TAB_ORDER,
  embedded = false,
  onClose,
  onSent
}) => {
  const dispatch = useDispatch();
  const [pending, setPending] = useState(null);
  const sawLoading = useRef(false);
  const onCloseRef = useRef(onClose);
  const onSentRef = useRef(onSent);
  const embeddedRef = useRef(embedded);
  onCloseRef.current = onClose;
  onSentRef.current = onSent;
  embeddedRef.current = embedded;

  const recipients = useMemo(() => {
    if (recipientsProp) {
      return recipientsProp;
    }
    return [{ objectId: personObjectId, name: candidateName, candidateObjectId }];
  }, [recipientsProp, personObjectId, candidateName, candidateObjectId]);

  const recipientIdsKey = recipients.map(recipient => recipient.objectId).filter(Boolean).join("|");
  const [checkedIds, setCheckedIds] = useState(() =>
    recipients.map(recipient => recipient.objectId).filter(Boolean)
  );
  const [selectedHistoryId, setSelectedHistoryId] = useState(
    () => recipients.find(recipient => recipient.objectId)?.objectId || ""
  );

  const handleSelectedIdsChange = useCallback(ids => {
    setCheckedIds(current => {
      if (current.length === ids.length && current.every((id, index) => id === ids[index])) {
        return current;
      }
      return ids;
    });
  }, []);

  useEffect(() => {
    const ids = recipientIdsKey ? recipientIdsKey.split("|") : [];
    setCheckedIds(ids);
    setSelectedHistoryId(ids[0] || "");
  }, [recipientIdsKey]);

  const checkedRecipients = useMemo(
    () => recipients.filter(recipient => checkedIds.includes(recipient.objectId)),
    [recipients, checkedIds]
  );

  const historyRecipient =
    checkedRecipients.find(recipient => recipient.objectId === selectedHistoryId) ||
    checkedRecipients[0] ||
    null;

  const messageObject = useMemo(() => {
    if (!historyRecipient?.objectId) {
      return null;
    }
    return {
      person: historyRecipient.objectId,
      candidate: historyRecipient.candidateObjectId
    };
  }, [historyRecipient]);

  const slice = useSelector(state => {
    if (!pending?.key) {
      return null;
    }
    return state?.driverReducer?.[SLICE_BY_PENDING[pending.key]] || null;
  }, shallowEqual);

  useEffect(() => {
    if (!pending) {
      sawLoading.current = false;
      return;
    }
    if (slice?.isLoading) {
      sawLoading.current = true;
      return;
    }
    if (!sawLoading.current) {
      return;
    }
    if (slice?.resultData?.Data?.Message) {
      sawLoading.current = false;
      setPending(null);
      dispatch(clearSendBulkSMS());
      if (!embeddedRef.current) {
        onCloseRef.current();
      }
      onSentRef.current?.();
      return;
    }
    if (slice?.errorData) {
      setPending(null);
    }
  }, [pending, slice, dispatch]);

  const handleSubmit = payload => {
    const recipientIds = (payload.personObjectIds || []).filter(Boolean);
    if (!recipientIds.length) {
      return;
    }

    sawLoading.current = false;

    if (payload.mode === "freeText") {
      setPending({ key: "freeText", remainingIds: [] });
      dispatch(
        sendBulkSMS({
          smsContent: payload.smsContent,
          personObjectIds: recipientIds,
          AttemptAppMessage: payload.attemptAppMessage
        })
      );
      return;
    }

    if (payload.mode !== "template") {
      return;
    }

    if (payload.templateId === BOOKING_DETAILS || payload.templateId === REQUEST_HRS) {
      const sent = dispatchBookingTemplate(dispatch, payload.templateId, payload.bookingObjectIds);
      if (sent) {
        setPending({ key: payload.templateId, remainingIds: [] });
      }
      return;
    }

    const knownTemplate =
      payload.templateId === CALENDAR_SUMMARY || PERSON_TEMPLATE_ACTIONS[payload.templateId];
    if (!knownTemplate) {
      return;
    }

    setPending({ key: payload.templateId, remainingIds: [] });
    dispatchTemplate(dispatch, payload.templateId, recipientIds);
  };

  return (
    <SendMessageModal
      open
      embedded={embedded}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitting={Boolean(pending)}
      recipients={recipients}
      onSelectedIdsChange={handleSelectedIdsChange}
      bookingObjectIds={bookingObjectIds}
      hiddenTemplates={hiddenTemplates}
      defaultTab={defaultTab}
      tabOrder={tabOrder}
      smsHistory={
        recipients.length === 0 ? null : messageObject ? (
          <SmsHistory
            messageObject={messageObject}
            layout="panel"
            headerLeft={
              checkedRecipients.length > 1 ? (
                <Select
                  className="w-full"
                  dropdownStyle={{ zIndex: 2000 }}
                  value={historyRecipient.objectId}
                  onChange={setSelectedHistoryId}
                  options={checkedRecipients.map(recipient => ({
                    value: recipient.objectId,
                    label: recipient.name || recipient.objectId
                  }))}
                />
              ) : null
            }
          />
        ) : (
          <p className="m-0 text-sm text-text-muted">Select a recipient to see history</p>
        )
      }
    />
  );
};

export default CandidateTableMessageModal;
